import type { PostgrestError } from '@supabase/supabase-js';

/**
 * O que o banco recusou, traduzido para quem está na tela.
 *
 * Antes, qualquer falha no INSERT virava "Erro ao salvar lead no banco" — e o
 * caso mais comum (e-mail já cadastrado no projeto, índice
 * `idx_leads_unique_email_project`) ficava indistinguível de uma queda de
 * rede. A pessoa tentava de novo, falhava de novo, e concluía que o sistema
 * "hora funciona, hora não".
 */
export class LeadSaveError extends Error {
  readonly code: string;
  /** E-mail em conflito, quando o motivo é duplicidade — a tela usa para achar o lead existente. */
  readonly duplicateEmail?: string;

  constructor(message: string, code: string, duplicateEmail?: string) {
    super(message);
    this.name = 'LeadSaveError';
    this.code = code;
    this.duplicateEmail = duplicateEmail;
  }
}

/** Códigos do Postgres que sabemos explicar. */
const UNIQUE_VIOLATION = '23505';
const RLS_DENIED = '42501';
const RAISED_BY_TRIGGER = 'P0001'; // limite do plano (`leads_insert_check`)

export function toLeadSaveError(
  error: Pick<PostgrestError, 'code' | 'message' | 'details'> | null | undefined,
  lead: { email?: string | null },
): LeadSaveError {
  const code = error?.code ?? 'unknown';
  const detail = `${error?.message ?? ''} ${error?.details ?? ''}`;

  if (code === UNIQUE_VIOLATION && /email/i.test(detail)) {
    const email = (lead.email ?? '').trim();
    return new LeadSaveError(
      email
        ? `Já existe um lead com o e-mail ${email} neste projeto.`
        : 'Já existe um lead com este e-mail neste projeto.',
      code,
      email || undefined,
    );
  }
  if (code === UNIQUE_VIOLATION) {
    return new LeadSaveError('Este lead já existe no projeto.', code);
  }
  if (code === RLS_DENIED) {
    return new LeadSaveError(
      'Você não tem permissão para criar leads neste projeto. Peça a um administrador para revisar o seu papel.',
      code,
    );
  }
  if (code === RAISED_BY_TRIGGER) {
    // A mensagem do trigger já é para o usuário ("Limite do plano atingido…").
    return new LeadSaveError(error?.message || 'O plano atual não permite criar mais leads.', code);
  }
  return new LeadSaveError(
    `Não foi possível salvar o lead${error?.message ? `: ${error.message}` : ''}.`,
    code,
  );
}
